/**
 * Custom validators that live as top-level keys on every DataElement
 * elementSchema, alongside JSON Schema's standard keywords. These are
 * processed by the Go runtime (not by AJV) and are NOT part of JSON
 * Schema draft-2020-12.
 *
 * Audit of admin_buildex_dev (17 real DEs):
 *   masterDataMappings : 17/17  — every production DE
 *   dateCompare        :  2/17
 *   sameDay            :  1/17
 *   sumEquals          :  1/17
 *   maxTotalDuration   :  1/17
 *
 * Shapes (sourced from real production data, not invented):
 *
 *   masterDataMappings: {
 *     [fieldName]: {
 *       masterDataTableName: string,    // e.g. "a-i-project-master-data"
 *       masterDataFieldName: string,    // e.g. "project_reference_number"
 *     }
 *   }
 *
 *   dateCompare: [
 *     {
 *       type: "ARRAY" | "ROOT",
 *       arrayField?: string,            // required when type === "ARRAY"
 *       before: string,                 // field name
 *       after:  string,                 // field name
 *       operator: "lessThan" | string,  // single value seen in production
 *     }
 *   ]
 *
 *   sameDay: {
 *     arrayField: string,
 *     rootField:  string,
 *     itemField:  string,
 *   }
 *
 *   maxTotalDuration: {
 *     arrayField:   string,
 *     timeInField:  string,
 *     timeOutField: string,
 *     maxHours:     number,
 *   }
 *
 *   sumEquals: {
 *     fields: string[],                 // root-level numeric fields
 *     arrayField: string,
 *     arrayFieldProp: string,           // numeric prop on each array item
 *     total: number,
 *   }
 */

export const CUSTOM_VALIDATOR_KEYS = [
	'masterDataMappings',
	'dateCompare',
	'sameDay',
	'maxTotalDuration',
	'sumEquals',
];

// Top-level keys the editor recognizes and never treats as "additional"
// custom validators. Includes the JSON Schema 2020-12 standard set plus
// the keywords this builder already manages explicitly (x-allOf, the
// custom validators above, x-uitype/x-uioptions are leaf-level so they
// don't appear here).
const RESERVED_TOP_LEVEL_KEYS = new Set([
	// JSON Schema core
	'$schema', '$id', '$ref', '$comment', '$defs', '$anchor', '$dynamicAnchor', '$dynamicRef',
	'definitions',
	// JSON Schema validation
	'type', 'enum', 'const', 'format',
	'multipleOf', 'minimum', 'maximum', 'exclusiveMinimum', 'exclusiveMaximum',
	'minLength', 'maxLength', 'pattern',
	'maxItems', 'minItems', 'uniqueItems', 'items', 'contains', 'minContains', 'maxContains',
	'maxProperties', 'minProperties',
	'required', 'dependentRequired',
	// JSON Schema applicator
	'properties', 'patternProperties', 'additionalProperties', 'propertyNames',
	'allOf', 'anyOf', 'oneOf', 'not',
	'if', 'then', 'else', 'dependentSchemas',
	// JSON Schema metadata
	'title', 'description', 'default', 'examples', 'deprecated', 'readOnly', 'writeOnly',
	// Root-level extension metadata tracked by diffRootMetadata (NOT custom validators)
	'securityClassification', 'sensitivityClassification',
	// Builder extensions handled elsewhere
	'x-allOf',
	// Custom validators handled by the dedicated forms
	...CUSTOM_VALIDATOR_KEYS,
]);

/**
 * Decide whether a top-level schema key should surface in the
 * "Additional" custom-validator editor. Anything that isn't a known
 * JSON Schema keyword and isn't one of our managed keys is fair game —
 * users can author future runtime-side validators (or schema-level
 * metadata) without a frontend release.
 */
export function isAdditionalCustomKey(key) {
	if (typeof key !== 'string' || key === '') return false;
	return !RESERVED_TOP_LEVEL_KEYS.has(key);
}

/**
 * Pull the custom validator keys out of a schema. Missing keys come back
 * as `null` (not `undefined`) so the editor can drive presence/absence
 * UI off a stable shape.
 *
 * `additional` collects any other top-level keys that aren't part of
 * JSON Schema and aren't one of our managed extensions — that's where
 * future custom validators (added on the runtime side without a
 * frontend release) surface as user-editable entries.
 */
export function extractCustomValidators(schema) {
	const out = {};
	for (const k of CUSTOM_VALIDATOR_KEYS) {
		out[k] = (schema && schema[k] != null) ? schema[k] : null;
	}
	const additional = [];
	if (schema && typeof schema === 'object') {
		for (const [k, v] of Object.entries(schema)) {
			if (!isAdditionalCustomKey(k)) continue;
			additional.push({ key: k, value: v });
		}
	}
	out.additional = additional.length > 0 ? additional : null;
	return out;
}

/**
 * Merge a custom-validators record into a built schema. `null` entries
 * remove the key from the schema (so the user can clear a validator they
 * had previously authored). The function mutates `schema` and returns it.
 *
 * `additional` (if present) is a list of `{ key, value }` pairs that
 * become arbitrary top-level schema keys. Pre-existing additional keys
 * not in the new list are removed so the editor's "delete this
 * validator" action actually deletes from the schema.
 */
export function applyCustomValidatorsToSchema(schema, validators) {
	if (!schema || typeof schema !== 'object') return schema;
	if (!validators || typeof validators !== 'object') return schema;
	for (const k of CUSTOM_VALIDATOR_KEYS) {
		const v = validators[k];
		if (v == null) {
			delete schema[k];
		} else {
			schema[k] = v;
		}
	}

	const newAdditionalKeys = new Set();
	if (Array.isArray(validators.additional)) {
		for (const entry of validators.additional) {
			if (!entry || typeof entry !== 'object') continue;
			const key = (entry.key || '').trim();
			if (!key || !isAdditionalCustomKey(key)) continue;
			schema[key] = entry.value;
			newAdditionalKeys.add(key);
		}
	}
	for (const k of Object.keys(schema)) {
		if (isAdditionalCustomKey(k) && !newAdditionalKeys.has(k)) {
			delete schema[k];
		}
	}

	return schema;
}

/**
 * Compare two custom-validator records using stable JSON canonicalization.
 * Used by the Confirm Changes modal to detect edits.
 */
export function customValidatorsEqual(a, b) {
	const A = a || {};
	const B = b || {};
	for (const k of CUSTOM_VALIDATOR_KEYS) {
		if (JSON.stringify(A[k] ?? null) !== JSON.stringify(B[k] ?? null)) return false;
	}
	return true;
}

// ============ Per-validator sanitizers ============
//
// Each sanitizer accepts a partial editor record and produces either:
//   - a clean validator value to store in the schema, OR
//   - `null` if the editor record is empty / blank, meaning "no validator".
//
// Sanitizers do NOT throw on malformed input — they return a best-effort
// shape. The UI is responsible for surfacing errors before save.

export function sanitizeMasterDataMappings(record) {
	if (!record || typeof record !== 'object') return null;
	const out = {};
	for (const [fieldName, mapping] of Object.entries(record)) {
		if (!fieldName || !mapping || typeof mapping !== 'object') continue;
		const tableName = (mapping.masterDataTableName || '').trim();
		const fieldRef = (mapping.masterDataFieldName || '').trim();
		if (!tableName && !fieldRef) continue;
		out[fieldName] = {
			masterDataTableName: tableName,
			masterDataFieldName: fieldRef,
		};
	}
	return Object.keys(out).length > 0 ? out : null;
}

export function sanitizeDateCompare(rules) {
	if (!Array.isArray(rules)) return null;
	const out = rules
		.map((r) => {
			if (!r || typeof r !== 'object') return null;
			const type = r.type === 'ARRAY' ? 'ARRAY' : 'ROOT';
			const before = (r.before || '').trim();
			const after = (r.after || '').trim();
			const operator = (r.operator || '').trim();
			const arrayField = type === 'ARRAY' ? (r.arrayField || '').trim() : '';
			if (!before || !after || !operator) return null;
			if (type === 'ARRAY' && !arrayField) return null;
			const entry = { type, before, after, operator };
			if (type === 'ARRAY') entry.arrayField = arrayField;
			return entry;
		})
		.filter((r) => r !== null);
	return out.length > 0 ? out : null;
}

export function sanitizeSameDay(rec) {
	if (!rec || typeof rec !== 'object') return null;
	const arrayField = (rec.arrayField || '').trim();
	const rootField = (rec.rootField || '').trim();
	const itemField = (rec.itemField || '').trim();
	if (!arrayField && !rootField && !itemField) return null;
	if (!arrayField || !rootField || !itemField) return null;
	return { arrayField, rootField, itemField };
}

export function sanitizeMaxTotalDuration(rec) {
	if (!rec || typeof rec !== 'object') return null;
	const arrayField = (rec.arrayField || '').trim();
	const timeInField = (rec.timeInField || '').trim();
	const timeOutField = (rec.timeOutField || '').trim();
	const maxHours = typeof rec.maxHours === 'number'
		? rec.maxHours
		: (rec.maxHours === '' || rec.maxHours == null ? null : Number(rec.maxHours));
	const allBlank = !arrayField && !timeInField && !timeOutField && maxHours == null;
	if (allBlank) return null;
	if (!arrayField || !timeInField || !timeOutField || maxHours == null || Number.isNaN(maxHours)) {
		return null;
	}
	return { arrayField, timeInField, timeOutField, maxHours };
}

export function sanitizeSumEquals(rec) {
	if (!rec || typeof rec !== 'object') return null;
	const fields = Array.isArray(rec.fields)
		? rec.fields.map((f) => String(f || '').trim()).filter(Boolean)
		: [];
	const arrayField = (rec.arrayField || '').trim();
	const arrayFieldProp = (rec.arrayFieldProp || '').trim();
	const total = typeof rec.total === 'number'
		? rec.total
		: (rec.total === '' || rec.total == null ? null : Number(rec.total));
	const allBlank = fields.length === 0 && !arrayField && !arrayFieldProp && total == null;
	if (allBlank) return null;
	if (fields.length === 0 || !arrayField || !arrayFieldProp || total == null || Number.isNaN(total)) {
		// Partially filled — return null so the schema doesn't carry a
		// half-formed validator. The UI surfaces a warning separately.
		return null;
	}
	return { fields, arrayField, arrayFieldProp, total };
}

/**
 * Drop additional-validator entries that have an empty/invalid key, or a
 * value that fails JSON parse (the editor accepts JSON-as-text). Returns
 * `null` when the resulting list is empty.
 */
export function sanitizeAdditionalValidators(list) {
	if (!Array.isArray(list)) return null;
	const out = [];
	for (const entry of list) {
		if (!entry || typeof entry !== 'object') continue;
		const key = (entry.key || '').trim();
		if (!key || !isAdditionalCustomKey(key)) continue;
		// Accept either an already-parsed value (object/array/scalar) or
		// a raw JSON-text draft from the editor; only emit when the value
		// is non-undefined (`null` is a legitimate JSON value).
		if (entry.value === undefined) continue;
		out.push({ key, value: entry.value });
	}
	return out.length > 0 ? out : null;
}

/**
 * Apply all per-validator sanitizers to an editor record at once. Returns
 * a fresh object suitable for `applyCustomValidatorsToSchema`.
 */
export function sanitizeCustomValidators(editorRecord) {
	const r = editorRecord || {};
	return {
		masterDataMappings: sanitizeMasterDataMappings(r.masterDataMappings),
		dateCompare: sanitizeDateCompare(r.dateCompare),
		sameDay: sanitizeSameDay(r.sameDay),
		maxTotalDuration: sanitizeMaxTotalDuration(r.maxTotalDuration),
		sumEquals: sanitizeSumEquals(r.sumEquals),
		additional: sanitizeAdditionalValidators(r.additional),
	};
}
